/**
 * subject.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { AuthService } from "app/auth/auth.service";
import { BrandingService } from "app/common/branding.service";
import { ZoneConfigurationResponse } from "app/server/messages/metadata";
import { BehaviorSubject, Observable, Observer, Subject as RxSubject }
  from "rxjs/Rx";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { StepUpdate } from "../algorithms/management/pipeline/step-update";
import { Algorithm, AlgorithmStep } from "../algorithms/structure/";
import { UserService } from "../auth/identity/user.service";
import { AlertService } from "../common/alert.service";
import { MessageReceiver } from "../server/message-receiver";
import { MessageTag } from "../server/messages/";
import { CreateSubjectContextRequest, SubjectContextResponse }
  from "../server/messages/context-management/";
import {
  RelationGraphExpansionRequest,
  RelationGraphRequest,
  RelationGraphResponse,
  RelationGraphSubjectContextRequest,
  RelationshipsConfigurationResponse,
  SubjectContextRelationshipsResponse
} from "../server/messages/subjects/";
import { MessagingService } from "../server/messaging.service";
import { disconnectEmpty, disconnectValue, disconnectWith }
  from "../support/types/decorators";
import { ModalHelpers } from "../support/utilities";

import {
  MessageRelationEdge,
  MessageRelationNode,
  RelationEdge,
  RelationGraph,
  RelationNode
} from "./relation-graph";
import {
  Relationship,
  RelationshipData,
  RelationshipType,
  RelationshipTypeData,
  Role
} from "./relationship";
import { GroupSelectionContext, SelectGroupsModalComponent }
  from "./select-groups-modal.component";
import { Subject } from "./subject";
import { SubjectContextType } from "./subject-context-type";
import { SubjectReference } from "./subject-reference";
import { SelectionMode, SubjectSelectionContext, SubjectSelectorModalComponent }
  from "./subject-selector-modal.component";
import { ConfigurationService } from "app/configuration/configuration.service";

const INSTANT_NAV_ALGOS : string[] = ["a-first-check-wi"];

/**
 * The {@link SubjectService} manages subjects received by the client, including
 * loading, manipulating, and relating them.
 */
@Injectable()
export class SubjectService implements MessageReceiver
{
    constructor (
      protected messaging : MessagingService,
      protected algorithms : AlgorithmService,
      protected router : Router,
      protected modal : Modal,
      protected users : UserService,
      protected auth : AuthService,
      protected config : ConfigurationService
    )
    {
      this.messaging.ready.subscribe(
        (on : boolean) => on ? this.connected() : this.disconnected());
    }

    private readonly CHEATY_NO_REDIRECT_ADMINS : string[] = [
      // "tdsmith@safetyweb.org"
    ];

    /**
     * The behavior when the messaging service connects.
     */
    public connected () : void
    {
      this.messaging.receive(MessageTag.RSP_CFG_RELATIONSHIPS).subscribe(
        (rcr : RelationshipsConfigurationResponse) =>
          this.readRelationshipConfig(rcr));
      this.messaging.receive(MessageTag.RSP_SUBJECT_CONTEXT_RELATIONSHIPS)
        .subscribe(
          (scrr : SubjectContextRelationshipsResponse) =>
            this.readRelationships(scrr));
      this.messaging.receive(MessageTag.RSP_CFG_ZONE)
        .subscribe((msg : ZoneConfigurationResponse) => {
          if (
            Array.isArray(msg.algorithms) &&
            msg.algorithms.length === 1 &&
            (
              INSTANT_NAV_ALGOS.indexOf(msg.algorithms[0].name) >= 0
              || this.config.client.type === "guest"
            )
          ) {
              // this.auth.specialRedirectBehavior =
              setTimeout(() => {
                if (this.CHEATY_NO_REDIRECT_ADMINS.indexOf(
                  this.users.self.login) === -1) {
                    this.launchNew(
                        this.algorithms.findAlgorithm(msg.algorithms[0].name))
                  }
                }, 100);
            }
      });
    }

    /**
     * The behavior when the messaging service disconnects.
     */
    public disconnected () : void {/* decorator-generated */}

    /**
     * A map from canonical {@link Role} names to their definitions
     */
    @disconnectValue(null)
    private roleMap : Map<string, Role>;

    /**
     * The known relationship definitions
     */
    @disconnectEmpty()
    public relationshipTypes : RelationshipType[] = [];

    /**
     * The known role defintions
     */
    @disconnectEmpty()
    public roles : Role[] = [];

    /**
     * Read relationship and role definitions from an incoming configuration
     * message.
     */
    private readRelationshipConfig (rcr : RelationshipsConfigurationResponse)
      : void
    {
      this.roles = rcr.roles;
      this.roleMap = new Map(
        this.roles.map(
          (role : Role) => [role.name, role]
        ) as Array<[string, Role]>);
      this.relationshipTypes = rcr.relationships.map(
        (rtd : RelationshipTypeData) => ({
          name: rtd.name,
          displayName: rtd.displayName,
          sourceType: rtd.sourceType,
          targetType: rtd.targetType,
          sourceRoles: rtd.sourceRoles.map(
            (sRole : {role : string, partners : string[]}) =>
              ({
                role: this.roleMap.get(sRole.role),
                partners: sRole.partners.map(
                  (name : string) => this.roleMap.get(name))
              })),
          targetRoles: rtd.targetRoles.map(
            (tRole : {role : string, partners : string[]}) =>
              ({
                role: this.roleMap.get(tRole.role),
                partners: tRole.partners.map(
                  (name : string) => this.roleMap.get(name))
              }))
        }));

    }

    /**
     * Read incoming relationship data and apply it to the appropriate subject.
     */
    private readRelationships (scrr : SubjectContextRelationshipsResponse)
      : void
    {
      const subject : Subject = this.byServerId(scrr.subjectId);
      if (!subject)
      {
        console.error("Received relationship data for unknown subject!", scrr);
      }
      else
      {
        subject.relationships = scrr.relationships.map(
          (rd : RelationshipData) =>
            new Relationship(rd, this.relationshipTypes, this.roles, subject));
      }
    }

    /**
     * Anonymous arrow function for finding algorithms by their name. This is
     * passed to new Subjects to allow them to identify their algorithms but
     * avoid a circular dependency.
     */
    private resolver : ((name : string) => Algorithm) =
      ((name : string) => this.algorithms.findAlgorithm(name));

    /**
     * All the known subjects. Note that subjects only come into existence once
     * users interact with their reference. This *usually* means they are loaded
     * immediately after creation, but is not a guarantee.
     */
    @disconnectEmpty()
    public subjects : Subject[] = [];

    /**
     * Filter the known subjects to those the client believes are loaded on the
     * server.
     */
    public get loaded () : Subject[]
    {
      return this.subjects.filter((s : Subject) => s.loaded);
    }

    /**
     * Find a Subject by its opaque server identifier
     * @param id identifier in the server's cache
     */
    public byServerId (id : number) : Subject
    {
      const matching : Subject[] = this.subjects.filter(
        (s : Subject) => s.id === id);
      if (matching.length === 0)
      {
        return null;
      }
      if (matching.length === 1)
      {
        return matching[0];
      }
      console.error(`Found multiple subjects with ID ${id}`, matching);
      throw new Error(`Multiple local subjects have server ID ${id}`);
    }

    /**
     * The group IDs selected on the previous launch of the visibility selection
     * modal dialog. (This allows us to automatically re-select them.)
     */
    @disconnectEmpty()
    private lastGroupVisibilitySelection : number[];

    static FORCE_BYPASS_MODAL : boolean = true;

    /**
     * Select group IDs, either returning one if the current user only has a
     * singleton group, or opening a modal dialog for selecting among multiple.
     */
    private getGroupIds () : Observable<number[]>
    {
      if (this.users.self.groups.length === 1)
      {
        return Observable.of(this.users.self.groupIds());
      }
      else if (this.users.self.guest || this.users.self.groups.length === 0)
      {
        return Observable.of([]);
      }
      else
      {
        if (SubjectService.FORCE_BYPASS_MODAL) {
          return Observable.of([this.users.self.groupIds()[0]]);
        }

        return ModalHelpers.observeResult<number[]>(
          ModalHelpers.openCustom(
            this.modal,
            SelectGroupsModalComponent,
            {
              availableGroups: this.users.self.groups,
              initialSelection: this.lastGroupVisibilitySelection
            } as GroupSelectionContext
          )
        ).do((groupIds : number[]) => {
          if (Array.isArray(groupIds))
          {
            this.lastGroupVisibilitySelection = groupIds;
          }
        });
      }
    }

    /**
     * Create a {@link SubjectReference} for a brand new subject
     * @param groupIds The group visibility
     */
    private freshReference (type : SubjectContextType, groupIds : number[])
      : SubjectReference
    {
      return {
        id: -1,
        host: this,
        type: "new",
        request: new CreateSubjectContextRequest(type, groupIds),
        priority: 0
      };
    }

    private duplicateReference (type : SubjectContextType, groupIds : number[])
      : SubjectReference
    {
      return {
        id: -1,
        host: this,
        type: "new",
        request: new CreateSubjectContextRequest(type, groupIds, true),
        priority: 0
      };
    }

    /**
     * Request the server to create a new subject, and return it if successful.
     */
    public fresh (initial : Algorithm) : Observable<Subject>
    {
      const type : SubjectContextType = initial.contextType;
      return this.getGroupIds().map((groupIds : number[]) => {
        const s : Subject =
          this.fromReference(this.freshReference(type, groupIds));
        s.newlyCreated = true;
        s.initialAlgorithmTarget = initial;
        return s;
      });
    }

    public duplicate (initial : Algorithm) : Observable<Subject>
    {
      const type : SubjectContextType = initial.contextType;
      return this.getGroupIds().map((groupIds : number[]) => {
        const s : Subject =
          this.fromReference(this.duplicateReference(type, groupIds));
        s.newlyCreated = true;
        s.initialAlgorithmTarget = initial;
        return s;
      });
    }

    /**
     * Utility to create a new Subject from a reference.
     * @param ref SubjectReference
     */
    public fromReference (ref : SubjectReference) : Subject
    {
      return new Subject(
        this.messaging,
        this.resolver,
        ref
      );
    }

    public openReference (ref : SubjectReference) : Observable<Subject>
    {
      return this.open(this.fromReference(ref));
    }

    /**
     * Add a subject to the subject list, if it doesn't already exist there.
     */
    public add (subject : Subject) : void
    {
      const objectExists : boolean = this.subjects.indexOf(subject) > -1;
      if (objectExists)
      {
        console.error("This exact subject already exists in the list.", this);
        throw new Error("Exact subject already exists in the list!");
      }
      const idExists : boolean = this.byServerId(subject.id) !== null;
      if (idExists)
      {
        console.error("Subject with ID exists in the list.", this, subject);
        throw new Error("Service already has subject with that id!");
      }
      this.subjects.push(subject);
    }

    /**
     * Request that the {@link Subject} loads itself via one of its
     * {@link SubjectReferences}, and then act upon the load response. If the
     * subject was new (unrecognized by this service) read the summary and other
     * properties off the response and add the subject to our list. OTHERWISE,
     * find the matching entry we've already seen and merge the new data in.
     * @param subject The subject to load
     */
    private load (subject : Subject) : Observable<Subject>
    {
      return subject.load().map((scr : SubjectContextResponse) => {
        const existing : Subject = this.byServerId(scr.objectId);
        if (existing)
        {
          // merge the new identifiers
          subject.identifiers.forEach(
            (sr : SubjectReference) => existing.addRef(sr));
          existing.lastRefType = subject.lastRefType;
          if (subject.localCreated > existing.localCreated && subject.summary)
          {
            existing.summary = subject.summary;
          }
          existing.readSubjectResult(scr);
          existing.initialAlgorithmTarget = subject.initialAlgorithmTarget;
          // mark the load parameter as a clone
          subject.cloneOf = existing;
          return existing;
        }
        else
        {
          subject.readSubjectResult(scr);
          this.add(subject);
          return subject;
        }
      }).defaultIfEmpty(subject);
    }

    /**
     * Read nonconversational updates about a Subject. This usually occurs
     * after launching an algorithm that changes the SubjectContextType of a
     * previously-amorphous subject.
     *
     * DEFUNCT: We don't currently allow asynchronous unrequested updates to
     * a {@link Subject}, though the function may be useful in the future.
     */
    // private readAsyncSubject (scr : SubjectContextResponse) : void
    // {
    //   const existing : Subject = this.byServerId(scr.objectId);
    //   if (!existing)
    //   {
    //     console.warn("Received subject data for unknown id!", scr);
    //   }
    //   else
    //   {
    //     existing.readSubjectResult(scr);
    //   }
    // }

    /**
     * Add a subject to the service's list, load it, and (optionally) navigate
     * to its summary page.
     * @param subject The subject to load
     * @param navigate Whether to navigate to the summary page
     */
    public open (subject : Subject, navigate : boolean = true)
      : Observable<Subject>
    {
      return this.load(subject).do(
        (s : Subject) => {
          if (navigate)
          {
            const alg : Algorithm = s.initialAlgorithmTarget;
            if (alg)
            {
              s.initialAlgorithmTarget = null;
              this.algorithms.open(alg, s);
            }
            else
            {
              this.router.navigate(this.summaryRoute(s));
            }
          }
        });
    }

    /**
     * Create a new subject, load it, and visit it
     * @param algorithm Initial algorithm target
     */
    public launchNew (algorithm : Algorithm) : void
    {
      // debugger;
      this.fresh(algorithm).flatMap((s : Subject) => this.open(s)).subscribe();
    }

    public launchDuplicate (algorithm : Algorithm) : void
    {
      // debugger;
      this.duplicate(algorithm).flatMap((s : Subject) => this.open(s)).subscribe();
    }

    /**
     * Return the path to the standard subject summary page
     */
    private summaryRoute (subject : Subject) : any[]
    {
      // The summary page route
      return ["subject", subject.id];
    }

    /**
     * Launch the selector dialog with no output in the "recent" mode to explore
     * available subjects on the SubjectService.
     */
    public recentSubjectsDialog () : void
    {
      this.selectViaDialog("Recent Subjects", "recent", null, "Open", "md")
        .subscribe((s : Subject | null) => {
          if (s instanceof Subject)
          {
            this.open(s, true).subscribe();
          }
        });
    }

    /**
     * Launch a dialog to select & load a subject from a particular source, of:
     *  ◦ 'recent', a list of all known subjects on this service
     *  ◦ 'search', a compacted view of the search panel and results table
     *  ◦ 'watchlist', the user's primary watchlist
     * @param title Label for the dialog
     * @param mode Which source to select subjects from
     * @param onlyOfType Context type to limit selection to
     */
    public selectViaDialog (
      title : string,
      mode : SelectionMode = "recent",
      onlyOfType? : SubjectContextType,
      actionLabel? : string,
      modalSize? : string
    ) : Observable<Subject>
    {
      return ModalHelpers.observeResult<Subject>(
        ModalHelpers.openCustom(
          this.modal,
          SubjectSelectorModalComponent,
          {
            size: modalSize || "lg",
            list: this.subjects,
            onlyOfType,
            mode,
            title,
            actionLabel,
            dialogClass: "modal-dialog modal-full"
          } as SubjectSelectionContext
        )
      );
    }

    /**
     * Attempt to retrieve a new relationship graph for a subject.
     * @param subject The root of the relationship graph
     */
    public startRelationshipGraph (subject : Subject)
      : Observable<RelationGraph>
    {
      return this.messaging.send(new RelationGraphRequest(subject.id)).map(
        (rgr : RelationGraphResponse) => this.buildGraph(rgr));
    }

    /**
     * Add a new relationship either as a secondary or primary edge, depending
     * on whether a link already exists between the nodes.
     * @param graph Graph to add the edge to
     * @param mre Edge definition as received in the graph message
     */
    private addPossibleEdge (graph : RelationGraph, mre : MessageRelationEdge)
      : void
    {
      const samePair : RelationEdge = graph.edges.find(
        (edge : RelationEdge) =>
          (edge.source.id === mre.source && edge.target.id === mre.target));
      // NOTE: This find only needs to check the matching orientation (and not
      // "reversed" edges) because by merit of the way the graph is built, each
      // pair of nodes will only receive a particular orientation of edge (with
      // the source closer to the expansion request that triggered it). However
      // this is merely an interactional effect, so perhaps we should go to the
      // work of finding reversed edges and adding secondaries with swapped
      // parameters? TODO [tdhs]
      if (samePair)
      {
        if (samePair.label !== mre.sourceRole
            || samePair.reverseLabel !== mre.targetRole)
        {
          // If the role labels don't match the "primary" labels on the pair,
          // attempt to add the edge as a secondary connection. This method
          // already handles the case that we've already seen this secondary,
          // in which case it does nothing.
          samePair.addSecondary(mre);
        }
      }
      else
      {
        // Create an entirely new edge and add it to the graph.
        graph.addEdgeByIds(
          mre.source,
          mre.target,
          (a : RelationNode, b : RelationNode) => new RelationEdge(a, b, mre));
      }
    }

    /**
     * Create a new {@link RelationGraph} from a JSON response describing one.
     * @param rgr The JSON message containing a new relation graph definition
     */
    private buildGraph (rgr : RelationGraphResponse) : RelationGraph
    {
      const g : RelationGraph = new RelationGraph(
        rgr.nodes.map(
          (mrn : MessageRelationNode) => {
            const node : RelationNode = new RelationNode(mrn);
            if (mrn.nodeId === 0)
            {
              // This is a cheat relying on the server's labelling order where
              // the root will always be assigned id 0.
              node.alreadyExpanded = true;
            }
            return node;
          }),
        []
      );
      rgr.edges.forEach(
        (mre : MessageRelationEdge) => this.addPossibleEdge(g, mre));
      g.objectId = rgr.objectId;
      g.expand = (node : RelationNode) => this.expandGraph(g, node);
      return g;
    }

    /**
     * Request to open a node in a {@link RelationGraph} as a {@link Subject}
     * (following the standard load and navigate to summary process).
     * @param graph The graph holding the node
     * @param node The node to be opened
     */
    public openRelationGraphNode (graph : RelationGraph, node : RelationNode)
      : void
    {
      const relative : Subject = this.fromReference({
        id: node.id,
        host: graph,
        type: "graphRelative",
        request: new RelationGraphSubjectContextRequest(
          graph.objectId, node.id),
        priority: 10
      });
      // relative.summary = node.summary;
      this.open(relative, true).subscribe();
    }

    /**
     * Merge a new graph definition (usually from a successful expansion) in to
     * an existing {@link RelationGraph} and return the unioned output.
     * @param graph The existing relation graph to merge into (will be mutated)
     * @param exp The JSON graph extension definition
     */
    private mergeGraphExpansion (
      graph : RelationGraph,
      exp : RelationGraphResponse
    ) : RelationGraph
    {
      exp.nodes.forEach((mrn : MessageRelationNode) => {
        if (graph.nodes.find(
          (node : RelationNode) => node.id === mrn.nodeId))
        {
          // console.warn(`Found pre-existing node with id ${mrn.nodeId}`);
        }
        else
        {
          graph.addNode(new RelationNode(mrn));
        }
      });
      exp.edges.forEach((mre : MessageRelationEdge) => {
        this.addPossibleEdge(graph, mre);
      });
      graph.reinitialize = true;
      return graph;
    }

    /**
     * Request to expand a graph a certain number of edges outward from a
     * particular node.
     * @param graph The graph to expand
     * @param target The root node to expand outward from
     * @param depth The number of links to traverse during expansion
     */
    public expandGraph (
      graph : RelationGraph,
      target : RelationNode,
      depth : number = 1
    ) : void
    {
      this.messaging.converse(
        new RelationGraphExpansionRequest(
          graph.objectId,
          target.id,
          depth
      )).subscribe(
        (rgr : RelationGraphResponse) => {
          target.alreadyExpanded = true;
          this.mergeGraphExpansion(graph, rgr);
        });
    }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/subjects/subject.service.ts